'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface InteractiveQuizProps {
  questions: QuizQuestion[];
  onComplete: (score: number) => void;
}

const InteractiveQuiz = ({ questions, onComplete }: InteractiveQuizProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 shadow-natural">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-3/4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;

  const handleAnswerSelect = (index: number) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) return;
    
    setShowExplanation(true);
    if (selectedAnswer === question.correctAnswer) {
      setScore(score + 1);
    }
  };

  const handleNext = () => {
    if (isLastQuestion) {
      onComplete(score + (selectedAnswer === question.correctAnswer ? 1 : 0));
    } else {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-natural">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-bold text-xl text-text-primary">Test Your Knowledge</h3>
        <span className="text-sm font-medium text-text-secondary">
          Question {currentQuestion + 1} of {questions.length}
        </span>
      </div>
      
      <div className="mb-6">
        <p className="text-lg text-text-primary font-medium mb-6">{question.question}</p>
        
        <div className="space-y-3">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = index === question.correctAnswer;
            const showResult = showExplanation;
            
            return (
              <button
                key={index}
                onClick={() => handleAnswerSelect(index)}
                disabled={showExplanation}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-300 ${
                  showResult
                    ? isCorrect
                      ? 'border-conversion bg-conversion/10'
                      : isSelected
                      ? 'border-error bg-error/10' :'border-border bg-surface'
                    : isSelected
                    ? 'border-primary bg-primary/10' :'border-border bg-surface hover:border-primary/50 hover:bg-surface'
                } ${showExplanation ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-text-primary">{option}</span>
                  {showResult && (
                    <Icon
                      name={isCorrect ? 'CheckCircleIcon' : isSelected ? 'XCircleIcon' : 'MinusCircleIcon'}
                      size={24}
                      className={isCorrect ? 'text-conversion' : isSelected ? 'text-error' : 'text-muted-foreground'}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {showExplanation && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0" />
            <div>
              <p className="font-medium text-text-primary mb-1">Explanation</p>
              <p className="text-sm text-text-secondary">{question.explanation}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-end space-x-4">
        {!showExplanation ? (
          <button
            onClick={handleSubmit}
            disabled={selectedAnswer === null}
            className={`px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 ${
              selectedAnswer === null
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-natural hover:shadow-natural-lg'
            }`}
          >
            Submit Answer
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="px-6 py-3 bg-conversion text-conversion-foreground rounded-lg font-heading font-semibold hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300"
          >
            {isLastQuestion ? 'Complete Quiz' : 'Next Question'}
          </button>
        )}
      </div>
    </div>
  );
};

export default InteractiveQuiz;